import { Button, Panel, StatusMessage, TextField } from "@hexling/ui";
import { Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { PageTitle } from "../../components/Layout";
import { useServices } from "../../data/AppData";

type Mode = "sign-in" | "sign-up" | "forgot";

const MIN_PASSWORD = 10;

export function SignInPage({ initialMode = "sign-in" }: { initialMode?: Mode }) {
  const { auth, api, user } = useServices();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const providers =
    useQuery({
      queryKey: ["config"],
      queryFn: () =>
        api
          .get<{ providers: string[] }>("/api/config")
          .then((c) => c.providers)
          .catch(() => [] as string[]),
    }).data ?? [];
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["me"] });
    // New sign-ins start syncing so the grimoire is backed up straight away.
    await user.updateSettings({ syncEnabled: true });
    await navigate({ to: "/" });
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!auth) return;
    setError(null);
    setBusy(true);
    try {
      if (mode === "forgot") {
        const res = await auth.requestPasswordReset({
          email,
          redirectTo: `${window.location.origin}/reset-password`,
        });
        if (res.error) throw new Error(res.error.message);
        setStatus("If that email has an account, a reset link is on its way.");
        return;
      }
      const res =
        mode === "sign-up"
          ? await auth.signUp.email({ email, password, name: name.trim() || email.split("@")[0]! })
          : await auth.signIn.email({ email, password });
      if (res.error) throw new Error(res.error.message);
      await done();
    } catch (err) {
      setError(
        err instanceof Error && err.message
          ? err.message
          : "That didn't work. Check your details and try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const social = async (provider: "google" | "apple") => {
    await auth?.signIn.social({ provider, callbackURL: `${window.location.origin}/` });
  };

  const title =
    mode === "sign-up"
      ? "Create your account"
      : mode === "forgot"
        ? "Reset your password"
        : "Sign in";
  const action =
    mode === "sign-up" ? "Create account" : mode === "forgot" ? "Send reset link" : "Sign in";

  return (
    <>
      <PageTitle
        title={title}
        subtitle="Accounts back up your grimoire and sync it across devices."
        back={{ to: "/settings", label: "Settings" }}
      />
      <Panel className="auth-panel">
        {mode !== "forgot" && providers.length > 0 && (
          <>
            <div className="auth-social">
              {providers.includes("apple") && (
                <Button variant="secondary" onClick={() => social("apple")}>
                  Continue with Apple
                </Button>
              )}
              {providers.includes("google") && (
                <Button variant="secondary" onClick={() => social("google")}>
                  Continue with Google
                </Button>
              )}
            </div>
            <p className="divider-text">or use your email</p>
          </>
        )}
        <form onSubmit={submit} noValidate>
          {mode === "sign-up" && (
            <TextField
              label="Your name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={80}
            />
          )}
          <TextField
            label="Email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {mode !== "forgot" && (
            <TextField
              label="Password"
              type="password"
              autoComplete={mode === "sign-up" ? "new-password" : "current-password"}
              hint={mode === "sign-up" ? `At least ${MIN_PASSWORD} characters` : undefined}
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
          {error && (
            <p className="hx-field__error" role="alert">
              {error}
            </p>
          )}
          <Button
            type="submit"
            busy={busy}
            disabled={
              !email ||
              (mode !== "forgot" && password.length < (mode === "sign-up" ? MIN_PASSWORD : 1))
            }
          >
            {action}
          </Button>
          <StatusMessage>{status}</StatusMessage>
        </form>
        <div className="auth-switch">
          {mode === "sign-in" && (
            <>
              <button type="button" className="link-button" onClick={() => setMode("sign-up")}>
                New here? Create an account
              </button>
              <button type="button" className="link-button" onClick={() => setMode("forgot")}>
                Forgot your password?
              </button>
            </>
          )}
          {mode !== "sign-in" && (
            <button type="button" className="link-button" onClick={() => setMode("sign-in")}>
              Back to sign in
            </button>
          )}
        </div>
      </Panel>
    </>
  );
}

export function ResetPasswordPage({ token }: { token?: string }) {
  const { auth } = useServices();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  if (!token) {
    return (
      <>
        <PageTitle title="This reset link has expired" />
        <p>
          Request a new one from <Link to="/sign-in">the sign-in page</Link>.
        </p>
      </>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const res = await auth!.resetPassword({ newPassword: password, token });
    if (res.error) setError(res.error.message ?? "That link didn't work. Request a new one.");
    else setDone(true);
  };

  return (
    <>
      <PageTitle title="Choose a new password" />
      {done ? (
        <p>
          Password updated. <Link to="/sign-in">Sign in</Link> with your new password.
        </p>
      ) : (
        <form onSubmit={submit}>
          <TextField
            label="New password"
            type="password"
            autoComplete="new-password"
            hint={`At least ${MIN_PASSWORD} characters`}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={error}
          />
          <Button type="submit" disabled={password.length < MIN_PASSWORD}>
            Update password
          </Button>
        </form>
      )}
    </>
  );
}
